import { ringWinding } from '../geo'
import { MIN_RING_POINTS, ringsToPolygons } from '../polygonUnion'
import type { GeoArea, GeoPt } from '../types'
import { fromMercator, toMercator, type Pt2 } from './mercator'

/** One polygon as `polygon-clipping` takes it: closed rings, outer first, metres. */
export type ClipperPolygon = Pt2[][]

function closeRing(ring: Pt2[]): Pt2[] {
  const [fx, fy] = ring[0]
  const [lx, ly] = ring[ring.length - 1]
  return fx === lx && fy === ly ? ring : [...ring, [fx, fy]]
}

/**
 * Every drawable polygon part of the areas, projected. Hole ownership goes
 * through the shared `ringsToPolygons`, so a lake stays with the parcel it is
 * in; `outerCount` absent means every ring is an outer, the same reading every
 * other surface applies.
 */
export function areasToClipperPolygons(areas: readonly GeoArea[]): ClipperPolygon[] {
  return areas.flatMap((area) =>
    ringsToPolygons(area.rings ?? [], area.outerCount ?? (area.rings ?? []).length)
      .filter((poly) => poly[0] && poly[0].length >= MIN_RING_POINTS)
      .map((poly) => poly.map((ring) => closeRing(ring.map(toMercator)))),
  )
}

/**
 * The clipper's `MultiPolygon` back to this package's open lon/lat rings,
 * with winding normalised: the first ring counter-clockwise, every other
 * ring clockwise. `polygon-clipping` documents this as its own output
 * convention, but three consumers downstream of this module rely on it as an
 * invariant (MapLibre's ring classification, `keyholeRing`'s non-zero
 * annulus, the tests' `pointInPolygon`) — nothing here should be correct only
 * because a dependency happens to behave, so it is enforced explicitly
 * rather than trusted.
 */
export function clipperResultToGeo(result: readonly Pt2[][][]): GeoPt[][][] {
  return result.map((poly) =>
    poly.map((ring, i) => {
      const open = ring.length > 1 && ring[0][0] === ring[ring.length - 1][0] && ring[0][1] === ring[ring.length - 1][1]
        ? ring.slice(0, -1)
        : ring
      const geo = open.map(fromMercator)
      const winding = ringWinding(geo)
      const wantsPositive = i === 0
      // A zero-area ring (degenerate input) has no winding to correct.
      return winding !== 0 && winding > 0 !== wantsPositive ? geo.slice().reverse() : geo
    }),
  )
}
